import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useIsWide, WIDEST_QUERY } from '../../useIsWide';
import { ProfileScreen } from '../profiles/ProfileScreen';
import { ArrowLeft, Ban, Bell, Camera, Feather, BookOpen, Check, Clock, Eye, EyeOff, Gift, Hourglass, Images, Info, Lock, Mail, Maximize2, Minimize2, Mailbox, MailOpen, MapPin, PenLine, Phone, Plus, Search, Send, X, XCircle } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { useGetSessionQuery } from '../auth/api';
import {
  useMineQuery,
  useMyContactQuery,
  usePeerContactQuery,
  useRemoveContactMutation,
  useShareContactMutation,
  useStartMutation,
  type RelationshipWithPeer,
} from '../relationships/api';
import { useMarkInvitedMutation, useMeQuery, useSearchQuery } from '../profiles/api';
import { TabBar } from '../../TabBar';
import { Avatar } from '../profiles/avatar';
import { useInviteFriendMutation } from '../mail/api';
import { useUnlockHiddenMutation } from '../relationships/api';
import { HiddenPill, HideCard, VaultBanner, looksLikeCode, useHiddenAutoLock, useVault } from '../hidden/HiddenUI';
import { BlockConfirm, RemoveOvertureButton, SetAsideCard, useSetAsidePair } from '../hidden/SetAsideUI';
import {
  BioCard,
  HistoryCard,
  TierLadder,
  sortNewestFirst,
} from '../relationships/story';
import { RELATIONSHIP_REASON_COPY, TIER_LABELS, daysUntilBirthday, isVisibleToRecipient, tasterPhrase, type RelationshipTier } from '@toiyp/shared';
import { useAppSettings, useLetterReadiness } from '../settings/api';
import { usePasteGuard } from './usePasteGuard';
import { forgetDraft, loadDrafts, persistDraft, updated } from '../drafts/draftsSlice';
import { composeMsOf } from '../drafts/storage';
import { enqueueSeal, flushPendingSeals } from '../offline/sealQueueSlice';
import { NotificationInvite, shouldInviteToNotifications } from '../notifications/NotificationInvite';
import { type AskKind, type AskReason, mayAsk, takeArrivalEmail } from '../notifications/askPolicy';
import { InstallCard } from '../install/InstallCard';
import { AcknowledgedNote } from '../personal/Acknowledge';
import { MetTag } from '../personal/Met';
import { ComposerNotes, NotesCard } from '../personal/Notes';
import { LetterSignature } from '../personal/Signature';
import { arrivesPhrase } from './eta';
import { FrankedStamp, PostageStamp } from '../postage/Stamp';
import { STAMP_BOOK_HASH } from '../postage/StampBook';
import { LetterStamp, PostageOfferModal, sealLabel, usePostageChoice } from '../postage/Postage';
import { ComposeStamp } from '../postage/StampPicker';
import { GiftBookLink, GiftStampBook } from '../postage/GiftStampBook';
import { ScrapbookCard, ScrapbookScreen, useScrapbook } from '../photos/Scrapbook';
import { keptPhotos, photoCount } from '../photos/scrapbook';
import { PaperPreviewButton } from '../stationery/PaperPreview';
import { Envelope } from '../stationery/Envelope';
import { sealPause } from '../postage/bookMemory';
import { downgradeNotice, postageNotice } from '../postage/notice';
import {
  useInboxQuery,
  useOpenMutation,
  useOutboxQuery,
  useTouchMutation,
  useSealMutation,
  useThreadQuery,
  useMyStampsQuery,
  useStampParcelsQuery,
  type ApiLetter,
  type StampParcel,
} from './api';
import {
  bubbleStamp,
  ComposeProgress,
  Countdown,
  formatFullStamp,
  formatStamp,
  InThePost,
  Journey,
  LetterRow,
  OverturePill,
  PostageModal,
  SavedAgo,
  apiErrorMessage,
  SentimentPill,
  StatePill,
} from './ui';

/* ── Conversation state machine ──────────────────────────────────────────── */

/**
 * Determines what action is currently available in a conversation.
 *
 *  empty              → no letters: write the first one
 *  my-in-transit      → last letter is mine, still traveling: wait
 *  my-awaiting-reply  → my last letter arrived, no response yet: nudge only
 *  their-in-transit   → their last letter is traveling: wait
 *  their-arrived      → their letter has arrived: reply (from detail screen)
 *  settled            → last exchange was answered: start a new letter
 */
type ConversationStatus =
  | 'empty'
  | 'my-in-transit'
  | 'my-awaiting-reply'
  | 'their-in-transit'
  | 'their-arrived'
  | 'settled'
  /** An overture is waiting on the member's accept/decline, not on a reply. */
  | 'overture-pending'
  /** The member refused this address: readable, but nothing can be written. */
  | 'overture-declined';

function getConversationStatus(
  letters: ApiLetter[],
  myId: string | undefined,
): ConversationStatus {
  if (!myId || letters.length === 0) return 'empty';
  const last = letters[letters.length - 1]!;
  const mine = last.authorId === myId;

  // An overture is governed by its decision, not by the delivery state machine:
  // a declined one is settled forever (until undone), and a pending one is
  // waiting on a person. Neither can be written to.
  if (last.kind === 'overture' && !mine) {
    return last.overtureDecision === 'declined' ? 'overture-declined' : 'overture-pending';
  }

  if (last.state === 'answered' || last.state === 'cancelled') return 'settled';
  if (mine && (last.state === 'sealed' || last.state === 'in_transit')) return 'my-in-transit';
  if (mine && (last.state === 'delivered' || last.state === 'opened')) return 'my-awaiting-reply';
  if (!mine && (last.state === 'sealed' || last.state === 'in_transit')) return 'their-in-transit';
  if (!mine && (last.state === 'delivered' || last.state === 'opened')) return 'their-arrived';
  return 'settled';
}

/** A new letter can be started only as the first one, or after the last exchange settled. */
function canWriteLetter(status: ConversationStatus): boolean {
  return status === 'empty' || status === 'settled';
}

/* ── Types ───────────────────────────────────────────────────────────────── */

import { ShareInviteLink } from '../inviteLinks/ShareInviteLink';
import { trackLetterSealed } from '../onboarding/track';
import { DeskTabBar, HomePanel, MobileDeskScreen, MobileDeskStrip, MobileNudge, useDeskHasNew } from '../home/HomePanel';
import { DeliveredPrompt } from '../home/DeliveredPrompt';
import { useShortScreen } from '../home/shortScreen';
import { trackHomePanelAction, useHomePanelVariant, type HomePanelVariant } from '../home/variant';
import { InviteChoices } from '../onboarding/InviteChoices';
import { hashToRoute, isAppScreenHash, routeKey, routeToHash, type Route } from './route';

interface ConversationSummary {
  relationshipId: string;
  /** Behind my secret code — only ever true while the vault is open. */
  hidden: boolean;
  peer: RelationshipWithPeer['peer'];
  last: ApiLetter | null;
  lastMine: boolean;
  eta: string | null;
  /** The exact delivery stamp of the letter currently on its way (F10 reveal). */
  arrival: string | null;
  reads: number | null;
  sentiment: string | null;
  unread: boolean;
  /** Arrived-but-unopened letters from the peer — the row's count badge. */
  unreadCount: number;
  /**
   * The conversation's last word is an overture — either unanswered first
   * contact, or a refusal. The row says so, because the two directions of it
   * (wait for a decision / you refused) are the whole state of the thread.
   */
  overture: { kind: 'overture'; decision: ApiLetter['overtureDecision'] } | null;
  /** Derived relationship tier (RT-1). */
  tier: RelationshipTier;
  tierReasons: string[];
  capabilities: string[];
  /** Derived engagement (engagement-v1) — powers the most-engaged avatar row. */
  engagement: number | null;
  /** A letter-in-progress for this person, saved locally (autosave). */
  draft: { body: string; updatedAt: number } | null;
  /** They have started a reply to my arrived letter (the pen). */
  peerWriting: boolean;
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function peerName(peer: RelationshipWithPeer['peer']): string {
  return peer?.handle ?? peer?.displayName ?? 'someone';
}

function humanEta(deliverAt: string | null): string {
  if (!deliverAt) return '';
  const mins = Math.round((new Date(deliverAt).getTime() - Date.now()) / 60000);
  if (mins <= 0) return 'arriving';
  if (mins < 60) return `${mins} min`;
  const hours = Math.round(mins / 60);
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
}


/* ── Root messages component ─────────────────────────────────────────────── */

export function Messages({
  onOpenProfile,
  profileOpen = false,
  onCloseProfile = () => {},
}: {
  onOpenProfile: () => void;
  /** Wide layout only: your profile is showing in the side panel. */
  profileOpen?: boolean;
  onCloseProfile?: () => void;
}) {
  const wide = useIsWide();
  const { data: sessionUser } = useGetSessionQuery();
  const { data: inbox } = useInboxQuery();
  const { data: outbox } = useOutboxQuery();
  const { data: relationships } = useMineQuery();
  const drafts = useAppSelector((s) => s.drafts.byId);
  const dispatch = useAppDispatch();
  const [route, setRoute] = useState<Route>({ name: 'conversations' });
  const [adding, setAdding] = useState(false);
  // The home panel test reaches phones too: a desk strip on the list, or a Desk tab.
  const { variant: homeVariant } = useHomePanelVariant();
  const short = useShortScreen();
  useHiddenAutoLock();

  // Asking to be told about letters (askPolicy.ts), at most once per visit:
  // straight away when they came from the arrival email, otherwise when a
  // letter to them is on its way, which is when hearing about it matters.
  const [reach, setReach] = useState<{ reason: AskReason; peer: string | null; kind: AskKind } | null>(null);
  const reachDecided = useRef(false);
  useEffect(() => {
    if (reachDecided.current || !inbox || !sessionUser) return;
    if (takeArrivalEmail()) {
      reachDecided.current = true;
      const kind = mayAsk({ spaced: false });
      if (kind) setReach({ reason: 'email', peer: null, kind });
      return;
    }
    const coming = inbox.find(
      (l) => (l.state === 'sealed' || l.state === 'in_transit') && l.authorId !== sessionUser.id,
    );
    // Nothing on its way yet: a live update may bring one, so keep watching.
    if (!coming) return;
    reachDecided.current = true;
    const kind = mayAsk();
    if (!kind) return;
    const rel = relationships?.find((r) => r.id === coming.relationshipId);
    // A pair kept hidden is never named, here as on a lock screen.
    setReach({ reason: 'incoming', peer: rel && !rel.hidden ? (rel.peer?.handle ?? null) : null, kind });
  }, [inbox, sessionUser, relationships]);
  const reachPrompt = reach && (
    <NotificationInvite
      reason={reach.reason}
      peerHandle={reach.peer}
      kind={reach.kind}
      onDone={() => setReach(null)}
    />
  );

  // The hash handler resolves peers from the freshest relationship list.
  const relationshipsRef = useRef(relationships);
  relationshipsRef.current = relationships;

  // Write the route into the hash (history stepping, deep links).
  useEffect(() => {
    const target = routeToHash(route);
    if (isAppScreenHash(window.location.hash)) return;
    if (window.location.hash !== target) window.location.hash = target;
  }, [route]);

  // Restore from the hash on load, and follow browser Back/forward.
  useEffect(() => {
    const onHash = () => {
      // App-level screens (the stamp book, the dashboard) own their hash; the
      // messenger keeps its route underneath rather than rewriting the URL.
      if (isAppScreenHash(window.location.hash)) return;
      const next = hashToRoute(window.location.hash, relationshipsRef.current);
      setRoute((prev) => (routeKey(prev) === routeKey(next) ? prev : next));
    };
    onHash();
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // A route restored from the URL has no peer object yet — resolve it from the
  // relationship list, falling back to the passed peer when available.
  const resolvePeer = (
    relationshipId: string | undefined,
    fallback?: RelationshipWithPeer['peer'],
  ): RelationshipWithPeer['peer'] =>
    fallback ?? relationships?.find((r) => r.id === relationshipId)?.peer ?? null;

  // Drafts are needed by the list itself (the "draft" pill), so load them at
  // the root rather than only when a compose box happens to mount.
  const draftsLoaded = useRef(false);
  useEffect(() => {
    if (draftsLoaded.current) return;
    draftsLoaded.current = true;
    void dispatch(loadDrafts());
  }, [dispatch]);

  const conversations = useMemo<ConversationSummary[]>(() => {
    const rels = (relationships ?? []).filter((r) => r.status === 'accepted');
    const all = [...(inbox ?? []), ...(outbox ?? [])];
    const myId = sessionUser?.id;
    return rels.map((r) => {
      const letters = all
        .filter((l) => l.relationshipId === r.id)
        .sort((a, b) => (a.sealedAt ?? '').localeCompare(b.sealedAt ?? ''));
      const last = letters[letters.length - 1] ?? null;
      const lastMine = Boolean(last && myId && last.authorId === myId);
      const onTheWay = letters.find((l) => l.state === 'sealed' || l.state === 'in_transit');
      const eta = onTheWay ? humanEta(onTheWay.deliverAt) : null;
      const arrival = onTheWay?.deliverAt ?? null;
      const reads =
        last && lastMine && (last.state === 'opened' || last.state === 'answered')
          ? last.reads.length
          : null;
      const draft = drafts[r.id];
      return {
        relationshipId: r.id,
        hidden: r.hidden ?? false,
        peer: r.peer,
        last,
        lastMine,
        eta,
        arrival,
        reads,
        sentiment: last?.quality?.sentiment ?? null,
        draft: draft && draft.body.trim().length > 0 ? { body: draft.body, updatedAt: draft.updatedAt } : null,
        unread: letters.some((l) => l.state === 'delivered' && l.authorId !== myId),
        unreadCount: letters.filter((l) => l.state === 'delivered' && l.authorId !== myId).length,
        overture:
          last?.kind === 'overture' ? { kind: 'overture', decision: last.overtureDecision } : null,
        tier: r.tier,
        tierReasons: r.tierReasons,
        capabilities: r.capabilities,
        engagement: r.engagement ?? null,
        peerWriting: Boolean(r.peerWriting) && lastMine,
      };
    });
  }, [relationships, inbox, outbox, sessionUser, drafts]);

  // The open screen. On a narrow screen it replaces the list; on a wide one it
  // fills the side panel beside it.
  let detail: ReactNode = null;
  if (route.name === 'summary') {
    detail = (
      <SummaryScreen
        relationshipId={route.relationshipId}
        peer={resolvePeer(route.relationshipId, route.peer)}
        onBack={() =>
          setRoute(route.back ?? { name: 'conversation', relationshipId: route.relationshipId })
        }
        onHidden={() => setRoute({ name: 'conversations' })}
        onOpenScrapbook={() =>
          setRoute({
            name: 'scrapbook',
            relationshipId: route.relationshipId,
            peer: route.peer,
            back: { name: 'summary', relationshipId: route.relationshipId, peer: route.peer, back: route.back },
          })
        }
      />
    );
  }
  if (route.name === 'scrapbook') {
    const scrapbookPeer = resolvePeer(route.relationshipId, route.peer);
    detail = (
      <ScrapbookScreen
        relationshipId={route.relationshipId}
        peerName={peerName(scrapbookPeer)}
        onBack={() =>
          setRoute(route.back ?? { name: 'conversation', relationshipId: route.relationshipId, peer: route.peer })
        }
        onOpenLetter={(letterId) =>
          setRoute({ name: 'letter', letterId, relationshipId: route.relationshipId, peer: route.peer })
        }
      />
    );
  }
  if (route.name === 'conversation') {
    detail = (
      <ConversationScreen
        relationshipId={route.relationshipId}
        peer={resolvePeer(route.relationshipId, route.peer)}
        onBack={() => setRoute({ name: 'conversations' })}
        onOpenSummary={() =>
          setRoute({
            name: 'summary',
            relationshipId: route.relationshipId,
            peer: route.peer,
            back: { name: 'conversation', relationshipId: route.relationshipId, peer: route.peer },
          })
        }
        onOpenScrapbook={() =>
          setRoute({
            name: 'scrapbook',
            relationshipId: route.relationshipId,
            peer: route.peer,
            back: { name: 'conversation', relationshipId: route.relationshipId, peer: route.peer },
          })
        }
        onOpenLetter={(l, opts) =>
          setRoute({
            name: 'letter',
            letterId: l.id,
            relationshipId: route.relationshipId,
            peer: route.peer,
            openNudge: opts?.openNudge,
          })
        }
      />
    );
  }
  if (route.name === 'letter') {
    detail = (
      <LetterDetailScreen
        letterId={route.letterId}
        relationshipId={route.relationshipId}
        peer={resolvePeer(route.relationshipId, route.peer)}
        openNudge={route.openNudge}
        onBack={() =>
          setRoute({
            name: 'conversation',
            relationshipId: route.relationshipId,
            peer: route.peer,
          })
        }
      />
    );
  }
  // Picking something from the list replaces whatever the panel showed —
  // including your own profile.
  const go = (r: Route) => {
    if (profileOpen) onCloseProfile();
    setRoute(r);
  };
  const list = (
    <ConversationsScreen
      conversations={conversations}
      activeId={wide && !profileOpen && 'relationshipId' in route ? route.relationshipId : null}
      onOpenProfile={onOpenProfile}
      onOpen={(c) =>
        go({ name: 'conversation', relationshipId: c.relationshipId, peer: c.peer })
      }
      onOpenSummary={(c) =>
        go({
          name: 'summary',
          relationshipId: c.relationshipId,
          peer: c.peer,
          back: { name: 'conversations' },
        })
      }
      onStarted={(rel) =>
        go({ name: 'conversation', relationshipId: rel.id, peer: rel.peer })
      }
      adding={adding}
      setAdding={setAdding}
      homeVariant={homeVariant}
      onOpenDesk={() => {
        trackHomePanelAction('noticeboard', 'open_desk', { surface: 'mobile_list' });
        go({ name: 'desk' });
      }}
      onShowDesk={() => go({ name: 'desk' })}
    />
  );

  if (!wide && route.name === 'desk' && homeVariant === 'noticeboard') {
    detail = (
      <MobileDeskScreen
        conversations={conversations}
        onOpenProfile={onOpenProfile}
        onWrite={() => {
          setRoute({ name: 'conversations' });
          setAdding(true);
        }}
        onOpenConversation={(relationshipId) => go({ name: 'conversation', relationshipId })}
        onLetters={() => setRoute({ name: 'conversations' })}
        short={short}
      />
    );
  }

  if (!wide) {
    return (
      <>
        {detail ?? list}
        {reachPrompt}
      </>
    );
  }
  return (
    <div className="split">
      {reachPrompt}
      <div className="split__list">{list}</div>
      <div className="split__panel">
        {profileOpen ? (
          <ProfileScreen onBack={onCloseProfile} />
        ) : (
          detail ?? (
            <HomePanel
              conversations={conversations}
              onOpenProfile={onOpenProfile}
              onWrite={() => setAdding(true)}
              onOpenConversation={(relationshipId) => go({ name: 'conversation', relationshipId })}
            />
          )
        )}
      </div>
    </div>
  );
}

/**
 * The letters list with nothing in it yet: a postie's shrug, and the ways to
 * ask someone for a letter right there.
 */
function EmptyLetterbox() {
  const { data: me } = useMeQuery();
  return (
    <div className="conversations__empty">
      <Mailbox size={40} className="conversations__empty-icon" aria-hidden="true" />
      <p className="conversations__empty-title">Nothing in the post yet.</p>
      <p>Who would you like to hear from?</p>
      {me?.handle && (
        <div className="conversations__empty-invite">
          <InviteChoices
            handle={me.handle}
            onStarted={(id) => {
              window.location.hash = `#/c/${id}`;
            }}
          />
        </div>
      )}
    </div>
  );
}


/* ── Level 1.5 — write to a new contact ─────────────────────────────────── */

function NewConversation({
  alreadyConnected,
  onStarted,
  open,
  setOpen,
  docked = false,
}: {
  alreadyConnected: Set<string>;
  onStarted: (rel: RelationshipWithPeer) => void;
  /** Lifted so the "Me +" avatar in the story row can open it too. */
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Wide layout: a compose button at the top of the list, not a floating one. */
  docked?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [start, { isLoading: starting }] = useStartMutation();
  const [error, setError] = useState<string | null>(null);

  // Debounce the search input a little.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const q = debounced.length >= 2 ? debounced : '';
  const { data: results, isFetching } = useSearchQuery(q, { skip: !q });

  // The search box doubles as the vault's keyhole: whatever is typed is quietly
  // tried as the secret code too. A miss looks exactly like a handle search.
  const [tryCode] = useUnlockHiddenMutation();
  const { isOpen: vaultOpen, open: openVault } = useVault();
  useEffect(() => {
    if (!open || vaultOpen || !looksLikeCode(debounced)) return;
    let cancelled = false;
    void tryCode({ code: debounced })
      .unwrap()
      .then((r) => {
        if (cancelled || !r.token || !r.expiresAt) return;
        openVault({ token: r.token, expiresAt: r.expiresAt });
        setQuery('');
        setOpen(false);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [debounced, open, vaultOpen, openVault, tryCode, setOpen]);

  // Invite someone by email (2026-02-10) — the panel's second way to connect.
  const [invite, { isLoading: inviting }] = useInviteFriendMutation();
  const [markInvited] = useMarkInvitedMutation();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteNote, setInviteNote] = useState('');
  const [inviteMsg, setInviteMsg] = useState<string | null>(null);
  const [inviteErr, setInviteErr] = useState<string | null>(null);
  const { minLetterChars } = useAppSettings();

  const onInvite = useCallback(async () => {
    if (!inviteEmail.trim()) return;
    setInviteErr(null);
    setInviteMsg(null);
    try {
      const note = inviteNote.trim();
      const res = await invite({ email: inviteEmail.trim(), message: note || undefined }).unwrap();
      void markInvited();
      setInviteEmail('');
      setInviteNote('');
      setInviteMsg(
        note.length >= minLetterChars
          ? `Invite sent to ${res.email} — your letter is on its way, and it will be waiting when they join.`
          : `Invite sent to ${res.email}. When they join, they can write to you.`,
      );
    } catch (e) {
      const err = e as { data?: { message?: string | string[] } };
      setInviteErr(Array.isArray(err.data?.message) ? err.data!.message[0]! : err.data?.message ?? 'Could not send the invite');
    }
  }, [invite, inviteEmail, inviteNote, minLetterChars, markInvited]);

  const onPick = useCallback(
    async (handle: string) => {
      setError(null);
      try {
        const rel = await start({ handle }).unwrap();
        setOpen(false);
        setQuery('');
        onStarted(rel);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'could not start a conversation');
      }
    },
    [start, onStarted],
  );

  return (
    <div className="new-conv">
      {!open ? (
        <button
          type="button"
          className={`new-conv__trigger ${docked ? 'new-conv__compose' : 'fab'}`}
          onClick={() => setOpen(true)}
        >
          <PenLine size={17} aria-hidden="true" />
          Write to someone new
        </button>
      ) : (
        <div className="new-conv__search">
          <div className="new-conv__field">
            <Search size={16} className="new-conv__field-icon" aria-hidden="true" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find them by @handle…"
              aria-label="Find someone by handle"
            />
            <button
              type="button"
              className="new-conv__close"
              onClick={() => {
                setOpen(false);
                setQuery('');
              }}
              aria-label="Close search"
            >
              <X size={16} />
            </button>
          </div>

          {error && <p className="error new-conv__error">{error}</p>}

          {q && (
            <div className="new-conv__results">
              {isFetching && <p className="muted">Checking the address book…</p>}
              {!isFetching && results?.length === 0 && (
                <p className="muted">No one by that name on our round. Check the spelling, or send them an invite.</p>
              )}
              {(results ?? []).map((p) => {
                const connected = alreadyConnected.has(p.handle);
                return (
                  <button
                    key={p.handle}
                    type="button"
                    className="new-conv__result"
                    disabled={connected || starting}
                    onClick={() => void onPick(p.handle)}
                  >
                    <span className="new-conv__avatar">
                    <Avatar name={p.displayName ?? p.handle} avatar={p.avatar ?? null} />
                  </span>
                    <span className="new-conv__info">
                      <span className="new-conv__name">@{p.handle}</span>
                      {p.displayName && <span className="muted">{p.displayName}</span>}
                    </span>
                    {connected ? (
                      <Check size={15} className="new-conv__check" aria-label="Already a contact" />
                    ) : starting ? (
                      <span className="muted">…</span>
                    ) : (
                      <span className="new-conv__go">write</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <ShareInviteLink />
          {/* Plus only: a stamp book that doubles as the invitation. */}
          <GiftBookLink newcomer label="Or welcome someone new with a stamp book" className="linkish gift-link new-conv__gift" />

          <div className="new-conv__invite">
            <span className="new-conv__invite-title">Or invite someone by email</span>
            <input
              className="field__input"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="friend@gmail.com"
              aria-label="Friend's email address"
              maxLength={254}
            />
            <textarea
              className="field__input new-conv__invite-note"
              rows={2}
              value={inviteNote}
              onChange={(e) => setInviteNote(e.target.value)}
              placeholder="A note to go with it (optional)"
              maxLength={280}
            />
            <button
              type="button"
              className="btn btn--ghost new-conv__invite-send"
              disabled={inviting || !inviteEmail.trim()}
              onClick={() => void onInvite()}
            >
              {inviting ? 'Sending…' : 'Send invite'}
            </button>
            {inviteMsg && (
              <p className="invite-friend__ok" role="status">
                {inviteMsg}
              </p>
            )}
            {inviteErr && (
              <p className="form-error invite-friend__err" role="alert">
                {inviteErr}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Level 1 — Conversations list ───────────────────────────────────────── */

function ConversationsScreen({
  conversations,
  activeId = null,
  onOpen,
  onOpenSummary,
  onStarted,
  onOpenProfile,
  adding,
  setAdding,
  homeVariant,
  onOpenDesk,
  onShowDesk,
}: {
  conversations: ConversationSummary[];
  /** Phones: which home panel variant decorates the list. */
  homeVariant: HomePanelVariant;
  onOpenDesk: () => void;
  /** The nudge's "See all" on noticeboard (tracked by the nudge itself). */
  onShowDesk: () => void;
  /** Wide layout: the conversation showing in the side panel. */
  activeId?: string | null;
  /** "Write to someone new" is open — lifted so the Getting started card can open it. */
  adding: boolean;
  setAdding: (open: boolean) => void;
  onOpenProfile: () => void;
  onOpen: (c: ConversationSummary) => void;
  onOpenSummary: (c: ConversationSummary) => void;
  onStarted: (rel: RelationshipWithPeer) => void;
}) {
  // The list is one ordering: newest first. Engagement lives in the avatar
  // row above it, not in the ordering of the rows (§5.2 F3 as shipped).
  const sorted = useMemo(() => sortNewestFirst(conversations), [conversations]);
  // Wide screens navigate from the app header and compose from the top of the
  // list; the floating pill and button are for thumbs.
  const wide = useIsWide();
  const deskHasNew = useDeskHasNew();
  // Short phones get one nudge in the list instead of the strip or the Desk bar.
  const short = useShortScreen();
  const noticeboard = !wide && !short && homeVariant === 'noticeboard';
  // Elsewhere, the one thing that card-slot carries is the delivered prompt.
  const nudge = !wide && short ? (
    <MobileNudge
      conversations={conversations}
      onOpenProfile={onOpenProfile}
      onWrite={() => setAdding(true)}
      onOpenConversation={(id) => {
        const c = conversations.find((x) => x.relationshipId === id);
        if (c) onOpen(c);
      }}
      onSeeAll={homeVariant === 'noticeboard' ? onShowDesk : onOpenProfile}
    />
  ) : (
    <DeliveredPrompt conversations={conversations} />
  );
  return (
    <section
      className={`screen${wide ? '' : ' screen--tabbed'}${noticeboard ? ' screen--desk-tabs' : ''} screen--home`}
      aria-label="Conversations"
    >
      {!wide && !short && homeVariant === 'desk' && (
        <MobileDeskStrip
          conversations={conversations}
          onOpenProfile={onOpenProfile}
          onWrite={() => setAdding(true)}
          onOpenConversation={(id) => {
            const c = conversations.find((x) => x.relationshipId === id);
            if (c) onOpen(c);
          }}
        />
      )}
      <EngagedRow
        conversations={conversations}
        onOpen={onOpenSummary}
        onAdd={() => setAdding(true)}
      />
      <div className="home-sheet">
      <NewConversation
        alreadyConnected={
          new Set(
            conversations.map((c) => c.peer?.handle).filter((h): h is string => Boolean(h)),
          )
        }
        onStarted={onStarted}
        open={adding}
        setOpen={setAdding}
        docked={wide}
      />
      <VaultBanner />
      <InstallCard />
      {/* Wide screens keep it in the side panel; on phones the desk strip or the Desk tab carries it. */}
      <div className="conversations-list">
        {sorted.length === 0 ? (
          <>
            <EmptyLetterbox />
            {nudge}
          </>
        ) : (
          sorted.map((c, i) => {
            const name = peerName(c.peer);
            // One excerpt shape for both branches. The peek preview arrives
            // already flattened at 140 chars, so it was the one path that could
            // hand the list a longer row than the delivered path.
            const snippet = c.last
              ? c.last.body.replace(/\s+/g, ' ').slice(0, 72)
              : 'A blank page, waiting for the first letter.';

            return (
              <Fragment key={c.relationshipId}>
              <button
                type="button"
                className={`conv-item${c.overture?.decision === 'declined' ? ' conv-item--declined' : ''}${c.relationshipId === activeId ? ' conv-item--active' : ''}`}
                aria-current={c.relationshipId === activeId ? 'true' : undefined}
                onClick={() => onOpen(c)}
              >
                <span className="conv-item__avatar">
                  <Avatar name={name} avatar={c.peer?.avatar ?? null} />
                  {c.eta && (
                    <span className="conv-item__arriving" role="img" aria-label="A letter is in the post">
                      <Mail size={10} strokeWidth={2.5} aria-hidden="true" />
                    </span>
                  )}
                </span>
                <span className="conv-item__body">
                  <span className="conv-item__top">
                    <span className="conv-item__headline">
                      <span className="conv-item__name">@{name}</span>
                      {c.last?.sealedAt && (
                        <span className="conv-item__time">{formatStamp(c.last.sealedAt)}</span>
                      )}
                    </span>
                    <span className="conv-item__pills">
                      {c.hidden && <HiddenPill />}
                      {c.overture && <OverturePill decision={c.overture.decision} />}
                      {c.draft && <DraftPill updatedAt={c.draft.updatedAt} />}
                      {c.peerWriting && <PenPill />}
                      <TierPill tier={c.tier} />
                      {c.sentiment && <SentimentPill sentiment={c.sentiment} />}
                      {c.eta && (
                        <span
                          className="pill pill--eta"
                          title={c.arrival ? `Arrives ${formatFullStamp(c.arrival)}` : undefined}
                        >
                          <Mail size={10} aria-hidden="true" /> arrives {c.arrival ? arrivesPhrase(c.arrival) : c.eta}
                        </span>
                      )}
                      {c.reads != null && (
                        <span className="pill pill--reads">read {c.reads} {c.reads === 1 ? 'time' : 'times'}</span>
                      )}
                    </span>
                  </span>
                  <span className="conv-item__snippet">
                    {c.draft
                      ? `${c.last && !c.lastMine ? 'Draft reply' : 'Draft'}: ${c.draft.body.trim()}`
                      : c.last
                        ? (c.lastMine ? '↑ ' : '↓ ')
                        : ''}
                    {c.draft ? '' : c.overture
                      ? c.overture.decision === 'declined'
                        ? 'Declined — they cannot write to you.'
                        : c.overture.decision === 'accepted'
                          ? 'Accepted — they have an invite.'
                          : 'Someone wrote to your address — waiting on your answer.'
                      : snippet}
                  </span>
                </span>
                {c.unreadCount > 0 ? (
                  <span
                    className="conv-item__count"
                    aria-label={`${c.unreadCount} unread letter${c.unreadCount === 1 ? '' : 's'}`}
                  >
                    {c.unreadCount}
                  </span>
                ) : null}
              </button>
              {i === 0 && nudge}
              </Fragment>
            );
          })
        )}
      </div>
      </div>
      {!wide &&
        (noticeboard ? (
          <DeskTabBar
            active="letters"
            onLetters={() => {}}
            onWrite={() => setAdding(true)}
            onDesk={onOpenDesk}
            hasNew={deskHasNew}
          />
        ) : (
          <TabBar active="letters" onLetters={() => {}} onProfile={onOpenProfile} />
        ))}
    </section>
  );
}

/* ── Level 1 — most engaged avatar row (F3 as shipped) ──────────────────── */

/**
 * The engagement surface: a row of the most engaged correspondents' avatars,
 * ordered most-engaged first. Tapping one opens the relationship summary.
 * The list below stays newest-first — engagement never reorders it.
 */
function EngagedRow({
  conversations,
  onOpen,
  onAdd,
}: {
  conversations: ConversationSummary[];
  onOpen: (c: ConversationSummary) => void;
  onAdd: () => void;
}) {
  const { data: me } = useMeQuery();
  const engaged = conversations
    .filter((c) => c.engagement != null)
    .sort((a, b) => (b.engagement ?? 0) - (a.engagement ?? 0))
    .slice(0, 8);

  return (
    <div className="engaged-row" role="group" aria-label="Most engaged conversations">
      {/* "Me" leads the row and is a shortcut to writing to someone new. */}
      <button
        type="button"
        className="engaged-row__item engaged-row__item--me"
        onClick={onAdd}
        aria-label="Write to someone new"
      >
        <span className="engaged-row__avatar">
          <Avatar name={me?.displayName ?? 'Me'} avatar={me?.avatar ?? null} />
          <span className="engaged-row__plus" aria-hidden="true">
            <Plus size={12} strokeWidth={3} />
          </span>
        </span>
        <span className="engaged-row__name">Me</span>
      </button>
      {/* Any engaged correspondent shows here. This used to wait for two, but
          hiding one of two pairs then emptied the whole row — and the
          remaining avatar is no less real for having no one to compare with. */}
      {engaged.map((c) => {
        const name = peerName(c.peer);
        return (
          <button
            key={c.relationshipId}
            type="button"
            className="engaged-row__item"
            title={`@${name}`}
            onClick={() => onOpen(c)}
          >
            <span className="engaged-row__avatar">
              <Avatar name={name} avatar={c.peer?.avatar ?? null} />
            </span>
            <span className="engaged-row__name">@{name}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ── Level 1 — relationship summary (F4/F5) ─────────────────────────────── */

/**
 * The relationship's own page: a tier strip, the bio card (fixed copy over
 * signals, depth-projected by the server) and, when the pair holds the
 * `history` capability, the history timeline. Reached by tapping an avatar —
 * never embedded in the message detail screen.
 */
function SummaryScreen({
  relationshipId,
  peer,
  onBack,
  onHidden,
  onOpenScrapbook,
}: {
  relationshipId: string;
  peer: RelationshipWithPeer['peer'];
  onBack: () => void;
  /** Hidden while the vault is closed — the pair has left the list. */
  onHidden: () => void;
  onOpenScrapbook: () => void;
}) {
  const { data: relationships } = useMineQuery();
  const { data: me } = useMeQuery();
  const name = peerName(peer);
  const rel = relationships?.find((r) => r.id === relationshipId);
  const canHistory = rel?.capabilities.includes('history') ?? false;
  // The Post Office is not someone you met, or someone to keep notes on.
  const { data: thread } = useThreadQuery({ relationshipId });
  const personable = !(thread ?? []).some((l) => l.kind === 'system-letter');

  return (
    <section className="screen" aria-label={`Relationship summary with @${name}`}>
      <header className="screen__header">
        <button type="button" className="screen__back" onClick={onBack} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <span className="screen__avatar" data-summary="">
          <Avatar name={name} avatar={peer?.avatar ?? null} />
        </span>
        <span className="screen__title">@{name}</span>
      </header>

      <TierStrip rel={rel} />

      <div className="summary-screen">
        {personable && <MetTag relationshipId={relationshipId} peerName={name} variant="card" />}
        {personable && <NotesCard relationshipId={relationshipId} peerName={name} />}
        <ScrapbookCard relationshipId={relationshipId} onOpen={onOpenScrapbook} />
        {personable && rel?.status === 'accepted' && (
          <GiftBookLink relationshipId={relationshipId} peerHandle={peer?.handle} className="linkish gift-link gift-link--rail" />
        )}
        <BioCard
          relationshipId={relationshipId}
          peerName={name}
          mineAvatar={me?.avatar ?? null}
          peerAvatar={peer?.avatar ?? null}
        />
        {canHistory && <HistoryCard relationshipId={relationshipId} enabled />}
        {rel && (
          <HideCard
            relationshipId={relationshipId}
            peerName={name}
            hidden={rel.hidden}
            onHidden={onHidden}
          />
        )}
        {rel && (
          <SetAsideCard
            relationshipId={relationshipId}
            peerName={name}
            canBlock={personable}
            onGone={onHidden}
          />
        )}
      </div>
    </section>
  );
}

/* ── Level 1.5 — relationship health (RT-1) ─────────────────────────────── */

function TierPill({ tier }: { tier: RelationshipTier }) {
  // Voice audit 2026-02-10: tiers are spoken as meaning, not classifier names.
  return (
    <span className={`pill pill--tier-${tier}`} title={TIER_LABELS[tier]}>
      {TIER_LABELS[tier]}
    </span>
  );
}

/** A locally-saved draft exists for this conversation — resume it, don't re-write. */
function DraftPill({ updatedAt }: { updatedAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  const secs = Math.max(0, Math.round((now - updatedAt) / 1000));
  const label = secs < 60 ? 'just now' : secs < 3600 ? `${Math.round(secs / 60)}m ago` : `${Math.round(secs / 3600)}h ago`;
  return (
    <span className="pill pill--draft" title={`Unsent draft, last saved ${label}`}>
      draft · {label}
    </span>
  );
}

/** Your unsent draft, shown where the letter will land: the end of the thread. */
function DraftBubble({
  relationshipId,
  body,
  updatedAt,
  isReply,
  onContinue,
}: {
  relationshipId: string;
  body: string;
  updatedAt?: number;
  isReply: boolean;
  onContinue: () => void;
}) {
  const dispatch = useAppDispatch();
  const words = body.split(/\s+/).filter(Boolean).length;
  return (
    <div className="draft-bubble" role="group" aria-label={isReply ? 'Your draft reply' : 'Your draft letter'}>
      <div className="draft-bubble__meta">
        <PenLine size={12} aria-hidden="true" />
        <span>{isReply ? 'Draft reply' : 'Draft letter'}</span>
        <span className="draft-bubble__dot" aria-hidden="true">·</span>
        <span>{words} {words === 1 ? 'word' : 'words'}</span>
        <span className="draft-bubble__dot" aria-hidden="true">·</span>
        <SavedAgo at={updatedAt} />
      </div>
      <p className="draft-bubble__excerpt">{body}</p>
      <div className="draft-bubble__actions">
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => {
            if (window.confirm('Discard this draft? It cannot be recovered.')) void dispatch(forgetDraft(relationshipId));
          }}
        >
          Discard
        </button>
        <button type="button" className="btn btn--primary" onClick={onContinue}>
          {isReply ? 'Finish reply' : 'Continue writing'}
        </button>
      </div>
    </div>
  );
}

/** They have a draft reply to my letter on their desk. Static on purpose: a pen, not a typing dot. */
function PenPill() {
  return (
    <span className="pill pill--pen" title="They've started a reply to your letter">
      <PenLine size={10} aria-hidden="true" /> writing back
    </span>
  );
}

/** The derived tier + its fixed-copy explanation, shown under the thread header. */
function TierStrip({ rel, children }: { rel: RelationshipWithPeer | undefined; children?: ReactNode }) {
  if (!rel) return null;
  const why = rel.tierReasons.map((k) => RELATIONSHIP_REASON_COPY[k] ?? k).filter(Boolean);
  return (
    <div className="tier-strip" role="status">
      <TierPill tier={rel.tier} />
      {why.length > 0 && <span className="tier-strip__why">{why.join(' ')}</span>}
      {children}
    </div>
  );
}

/**
 * Narrow screens: contact exchange lives behind a pill in the tier strip and
 * opens as a bottom sheet, so the letters keep the screen. The pill asks for
 * attention (wax colour, a dot when they've shared and you haven't) until your
 * details are in, then settles into a quiet "Contacts" you can come back to —
 * it never vanishes, because it is also where their details live.
 */
function ContactsPill({ relationshipId, peerName: name }: { relationshipId: string; peerName: string }) {
  const { data: mine } = useMyContactQuery({ relationshipId });
  const { data: peer } = usePeerContactQuery({ relationshipId });
  const [open, setOpen] = useState(false);
  const shared = Boolean(mine);
  const peerHas = Boolean(peer?.phone || peer?.externalEmail || peer?.address);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        className={`contacts-pill${shared ? '' : ' contacts-pill--ask'}`}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
      >
        <Phone size={12} aria-hidden="true" />
        {shared ? 'Contacts' : peerHas ? `@${name} shared — swap?` : 'Swap contacts'}
        {!shared && peerHas && <span className="contacts-pill__dot" aria-hidden="true" />}
      </button>
      {open && (
        <div className="sheet-backdrop" onClick={() => setOpen(false)}>
          <div
            className="sheet"
            role="dialog"
            aria-modal="true"
            aria-label={`Contact details with @${name}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sheet__grip" aria-hidden="true" />
            <button
              type="button"
              className="sheet__close"
              onClick={() => setOpen(false)}
              aria-label="Close contact details"
            >
              <X size={16} aria-hidden="true" />
            </button>
            <ContactsCard
              relationshipId={relationshipId}
              peerName={name}
              onSaved={() => setOpen(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}

/**
 * The birthday, as a prompt rather than a calendar entry.
 *
 * The server's daily sweep has usually pushed this already; the pill is for
 * whenever the member happens to look. Only `MM-DD` ever reaches the client,
 * so this can count the days but never show an age — and the same dial the
 * sweep obeys decides when it is early enough to be useful.
 */
function BirthdayPill({
  peerName: name,
  birthday,
  onWrite,
}: {
  peerName: string;
  /** Their birthday as `MM-DD`, or null when they have not set one. */
  birthday: string | null;
  /** Absent while a letter is in the post or awaiting a reply: no "Write" then. */
  onWrite?: () => void;
}) {
  const { birthdayRemindDaysBefore } = useAppSettings();
  const days = birthday ? daysUntilBirthday(birthday) : null;
  if (birthdayRemindDaysBefore <= 0 || days === null || days > birthdayRemindDaysBefore) return null;

  const when = days === 0 ? 'is today' : days === 1 ? 'is tomorrow' : `is in ${days} days`;
  return (
    onWrite ? (
      <button type="button" className="birthday-pill" onClick={onWrite}>
        <Gift size={12} aria-hidden="true" />
        {`@${name}'s birthday ${when}`}
        <span className="birthday-pill__cta">Write</span>
      </button>
    ) : (
      <span className="birthday-pill">
        <Gift size={12} aria-hidden="true" />
        {`@${name}'s birthday ${when}`}
      </span>
    )
  );
}

/**
 * Contact exchange (RT-3). Rendered only when the pair holds the `contact`
 * capability — the server enforces it too; this is presentation.
 */
function ContactsCard({
  relationshipId,
  peerName,
  onSaved,
}: {
  relationshipId: string;
  peerName: string;
  /** Called after a successful share/update — the mobile sheet closes on it. */
  onSaved?: () => void;
}) {
  const { data: mine } = useMyContactQuery({ relationshipId });
  const { data: peer } = usePeerContactQuery({ relationshipId });
  const [share, { isLoading: saving }] = useShareContactMutation();
  const [remove, { isLoading: removing }] = useRemoveContactMutation();
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mine) {
      setPhone(mine.phone ?? '');
      setEmail(mine.externalEmail ?? '');
      setAddress(mine.address ?? '');
      setDirty(false);
    }
  }, [mine]);

  const onSave = useCallback(async () => {
    setError(null);
    try {
      await share({
        relationshipId,
        phone: phone.trim() || undefined,
        externalEmail: email.trim() || undefined,
        address: address.trim() || undefined,
      }).unwrap();
      setDirty(false);
      onSaved?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save contact details');
    }
  }, [share, relationshipId, phone, email, address, onSaved]);

  const onRemove = useCallback(async () => {
    setError(null);
    try {
      await remove({ relationshipId }).unwrap();
      setPhone('');
      setEmail('');
      setAddress('');
      setDirty(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not revoke contact details');
    }
  }, [remove, relationshipId]);

  const peerHas = Boolean(peer?.phone || peer?.externalEmail || peer?.address);

  return (
    <div className="contacts-card">
      <div className="contacts-card__head">
        <span className="contacts-card__title">Contact details</span>
        <span className="contacts-card__hint">shared only with @{peerName}</span>
      </div>
      <label className="field">
        <span className="field__label">Phone</span>
        <input
          className="field__input"
          value={phone}
          onChange={(e) => { setPhone(e.target.value); setDirty(true); }}
          placeholder="+1 555 0100"
          maxLength={40}
        />
      </label>
      <label className="field">
        <span className="field__label">Email</span>
        <input
          className="field__input"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setDirty(true); }}
          placeholder="you@elsewhere.com"
          maxLength={254}
          type="email"
        />
      </label>
      <label className="field">
        <span className="field__label">Address</span>
        <input
          className="field__input"
          value={address}
          onChange={(e) => { setAddress(e.target.value); setDirty(true); }}
          placeholder="Where you actually live"
          maxLength={500}
        />
      </label>
      {error && (
        <p className="form-error" role="alert">
          <Info size={13} aria-hidden="true" /> {error}
        </p>
      )}
      <div className="contacts-card__actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={saving || (!dirty && Boolean(mine))}
          onClick={() => void onSave()}
        >
          {saving ? 'Saving…' : mine ? 'Update' : 'Share my details'}
        </button>
        {mine && (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={removing}
            onClick={() => void onRemove()}
          >
            {removing ? 'Revoking…' : 'Revoke'}
          </button>
        )}
      </div>

      {peerHas ? (
        <div className="contacts-card__peer">
          <span className="contacts-card__peer-label">@{peerName} has shared</span>
          {peer?.phone && <p className="contacts-card__peer-row"><Phone size={13} aria-hidden="true" /> {peer.phone}</p>}
          {peer?.externalEmail && <p className="contacts-card__peer-row"><Mail size={13} aria-hidden="true" /> {peer.externalEmail}</p>}
          {peer?.address && <p className="contacts-card__peer-row"><MapPin size={13} aria-hidden="true" /> {peer.address}</p>}
        </div>
      ) : (
        <p className="contacts-card__peer-empty">@{peerName} hasn&apos;t shared anything yet. That comes when they&apos;re ready.</p>
      )}
    </div>
  );
}

/* ── Level 2 — Thread (read-only, action bar is state-aware) ─────────────── */

function ConversationScreen({
  relationshipId,
  peer,
  onBack,
  onOpenSummary,
  onOpenScrapbook,
  onOpenLetter,
}: {
  relationshipId: string;
  peer: RelationshipWithPeer['peer'];
  onBack: () => void;
  onOpenSummary: () => void;
  onOpenScrapbook: () => void;
  onOpenLetter: (l: ApiLetter, opts?: { openNudge?: boolean }) => void;
}) {
  const { data: sessionUser } = useGetSessionQuery();
  const { data: relationships } = useMineQuery();
  const { data: thread, isLoading } = useThreadQuery({ relationshipId });
  const letters = useMemo(() => thread ?? [], [thread]);
  const { data: parcels } = useStampParcelsQuery({ relationshipId });
  const scrollRef = useRef<HTMLDivElement>(null);
  const [composing, setComposing] = useState(false);
  const draft = useAppSelector((s) => s.drafts.byId[relationshipId]);
  const draftBody = draft?.body.trim() ? draft.body.trim() : null;

  // The composer belongs to one correspondence; switching to another (the
  // wide list stays in view) closes it. The draft itself is kept.
  useEffect(() => {
    setComposing(false);
  }, [relationshipId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [letters.length, relationshipId, composing]);

  const name = peerName(peer);
  const myId = sessionUser?.id;
  const items = useMemo(() => threadItems(letters, parcels ?? [], myId), [letters, parcels, myId]);
  const status = getConversationStatus(letters, myId);
  const lastLetter = letters[letters.length - 1] ?? null;

  const rel = relationships?.find((r) => r.id === relationshipId);
  const { data: me } = useMeQuery();
  // Desktop-widest: letters on the left, the relationship's details beside them.
  const twoColumn = useIsWide(WIDEST_QUERY);

  // Status-aware info strip
  const infoStrip = (
    <ConversationInfoStrip
      status={status}
      peerName={name}
      acknowledged={Boolean(lastLetter?.authorId === myId && lastLetter?.acknowledgedAt)}
      peerWriting={Boolean(rel?.peerWriting)}
      fromPostOffice={lastLetter?.kind === 'system-letter'}
      relationshipId={relationshipId}
      onRemoved={onBack}
    />
  );
  // Notes and "where we met" are for people. The Post Office and a stranger's
  // undecided overture get neither.
  const personable = !letters.some((l) => l.kind === 'system-letter') &&
    status !== 'overture-pending' && status !== 'overture-declined';

  const thread_ = (
    <>
      <div className="thread__scroll" ref={scrollRef}>
        {/* Where you met opens the shared history — the story starts there. */}
        {!isLoading && personable && (
          <MetTag relationshipId={relationshipId} peerName={name} variant="thread" />
        )}
        {isLoading && <div className="thread__loading">Fetching letters…</div>}
        {!isLoading && letters.length === 0 && (
          <div className="thread__empty">
            <PenLine size={32} aria-hidden="true" />
            <p>A blank page, waiting.<br />Write the first letter below, and take your time.</p>
          </div>
        )}
        <div className="thread__list">
          {items.map((item, i) => {
            const day = dayLabel(item.at);
            const chip =
              day && day !== dayLabel(items[i - 1]?.at ?? null) ? (
                <span key={`day-${item.key}`} className="date-chip">{day}</span>
              ) : null;
            if (item.kind === 'parcel') {
              return [chip, <ParcelBubble key={item.key} parcel={item.parcel} peerName={name} />];
            }
            const l = item.letter;
            const mine = l.authorId === myId;
            // In its envelope while on the way, and until the recipient opens it.
            if (l.envelope && (l.state === 'sealed' || l.state === 'in_transit' || (!mine && l.state === 'delivered'))) {
              return [
                chip,
                <EnvelopeBubble
                  key={l.id}
                  letter={l}
                  mine={mine}
                  peerName={name}
                  myName={me?.handle ?? null}
                  onOpen={() => onOpenLetter(l)}
                />,
              ];
            }
            const incomingAndSealed =
              !mine && (l.peek || l.state === 'sealed' || l.state === 'in_transit');
            if (incomingAndSealed) {
              return [
                chip,
                <SealedBubble
                  key={l.id}
                  letter={l}
                  mine={mine}
                  onOpen={() => onOpenLetter(l)}
                />,
              ];
            }
            return [
              chip,
              <ReadableBubble
                key={l.id}
                letter={l}
                mine={mine}
                onOpen={() => onOpenLetter(l)}
              />,
            ];
          })}
          {draftBody && !composing && (status === 'empty' || status === 'settled' || status === 'their-arrived') && (
            <DraftBubble
              relationshipId={relationshipId}
              body={draftBody}
              updatedAt={draft?.updatedAt}
              isReply={status === 'their-arrived'}
              onContinue={() => {
                if (status === 'their-arrived') {
                  const theirs = letters.findLast((l) => l.authorId !== myId && (l.state === 'delivered' || l.state === 'opened'));
                  if (theirs) onOpenLetter(theirs);
                } else {
                  setComposing(true);
                }
              }}
            />
          )}
        </div>
      </div>

      {/* Action bar — what you can do right now */}
      <ThreadActionBar
        status={status}
        peerName={name}
        notes={personable}
        relationshipId={relationshipId}
        lastLetter={lastLetter}
        composing={composing}
        draftBody={draftBody}
        onCompose={() => setComposing(true)}
        onCancelCompose={() => setComposing(false)}
        onComposeDone={() => setComposing(false)}
        onNudge={() => lastLetter && onOpenLetter(lastLetter, { openNudge: true })}
        onOpenLetter={onOpenLetter}
        letters={letters}
        myId={myId}
      />
    </>
  );

  return (
    <section className="screen" aria-label={`Correspondence with @${name}`}>
      <header className="screen__header">
        <button
          type="button"
          className="screen__back"
          onClick={onBack}
          aria-label="Back to conversations"
        >
          <ArrowLeft size={20} />
        </button>
        {twoColumn ? (
          // The details are already beside the letters, so the avatar's ⋯
          // opens the things you do to the conversation rather than a page.
          <ConversationMenu
            peer={peer}
            name={name}
            relationshipId={relationshipId}
            rel={rel}
            onOpenSummary={onOpenSummary}
            onOpenScrapbook={onOpenScrapbook}
            onHidden={onBack}
            canBlock={!letters.some((l) => l.kind === 'system-letter')}
          />
        ) : (
          <button
            type="button"
            className="screen__avatar" data-summary=""
            onClick={onOpenSummary}
            aria-label={`Open @${name}'s relationship summary`}
          >
            <Avatar name={name} avatar={peer?.avatar ?? null} />
          </button>
        )}
        <span className="screen__title">@{name}</span>
      </header>

      {twoColumn ? (
        <div className="conv-columns">
          <div className="conv-columns__thread">
            {infoStrip}
            {thread_}
          </div>
          {/* Everything the summary page holds, beside the letters instead of
              a tap away: tier, contact exchange, the bio card, history, hiding. */}
          <aside className="conv-columns__details" aria-label={`About you and @${name}`}>
            {personable && (
              <BirthdayPill
                peerName={name}
                birthday={peer?.birthday ?? null}
                onWrite={canWriteLetter(status) ? () => setComposing(true) : undefined}
              />
            )}
            <TierStrip rel={rel} />
            {personable && rel?.status === 'accepted' && (
              <GiftBookLink relationshipId={relationshipId} peerHandle={peer?.handle} className="linkish gift-link gift-link--rail" />
            )}
            {personable && <NotesCard relationshipId={relationshipId} peerName={name} />}
            <ScrapbookCard relationshipId={relationshipId} onOpen={onOpenScrapbook} />
            <BioCard
              relationshipId={relationshipId}
              peerName={name}
              mineAvatar={me?.avatar ?? null}
              peerAvatar={peer?.avatar ?? null}
              showLocks={false}
            />
            {rel && (
              <TierLadder
                tier={rel.tier}
                reason={
                  rel.tierReasons.map((k) => RELATIONSHIP_REASON_COPY[k] ?? k).join(' ') || null
                }
                capabilities={rel.capabilities}
              />
            )}
            {rel?.capabilities.includes('contact') && (
              <ContactsCard relationshipId={relationshipId} peerName={name} />
            )}
            {rel?.capabilities.includes('history') && (
              <HistoryCard relationshipId={relationshipId} enabled />
            )}
          </aside>
        </div>
      ) : (
        <>
          {infoStrip}
          {/* Derived relationship tier (RT-1); contact exchange opens as a sheet
              from the strip. The bio and history live on the summary page,
              reached by tapping the avatar. */}
          <TierStrip rel={rel}>
            {personable && (
              <BirthdayPill
                peerName={name}
                birthday={peer?.birthday ?? null}
                onWrite={canWriteLetter(status) ? () => setComposing(true) : undefined}
              />
            )}
            {rel?.capabilities.includes('contact') && (
              <ContactsPill relationshipId={relationshipId} peerName={name} />
            )}
          </TierStrip>
          {thread_}
        </>
      )}
    </section>
  );
}

/**
 * Widest layout: the header avatar's ⋯ menu. Keeping a conversation private
 * is something you do now and then, so it waits here rather than taking a
 * card in the details column.
 */
function ConversationMenu({
  peer,
  name,
  relationshipId,
  rel,
  onOpenSummary,
  onOpenScrapbook,
  onHidden,
  canBlock,
}: {
  peer: RelationshipWithPeer['peer'];
  name: string;
  relationshipId: string;
  rel: RelationshipWithPeer | undefined;
  onOpenSummary: () => void;
  onOpenScrapbook: () => void;
  onHidden: () => void;
  /** Not the Post Office: it can be hidden, never blocked. */
  canBlock: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [confirmingBlock, setConfirmingBlock] = useState(false);
  const setAside = useSetAsidePair(relationshipId, () => {
    setOpen(false);
    setConfirmingBlock(false);
    onHidden();
  });
  const { photos } = useScrapbook(relationshipId);
  const [privacy, setPrivacy] = useState(false);
  const [gifting, setGifting] = useState(false);
  const { data: stamps } = useMyStampsQuery();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setPrivacy(false);
  }, [open]);

  return (
    <div className="conv-menu" ref={ref}>
      <button
        type="button"
        className="screen__avatar" data-summary=""
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More for this conversation"
      >
        <Avatar name={name} avatar={peer?.avatar ?? null} />
      </button>
      {open && (
        <div className="conv-menu__pop" role="menu" aria-label={`More for @${name}`}>
          {privacy && rel ? (
            <HideCard
              relationshipId={relationshipId}
              peerName={name}
              hidden={rel.hidden}
              onHidden={() => {
                setOpen(false);
                onHidden();
              }}
            />
          ) : (
            <>
              <button
                type="button"
                role="menuitem"
                className="conv-menu__item"
                onClick={() => setPrivacy(true)}
              >
                <Lock size={14} aria-hidden="true" />
                <span>
                  {rel?.hidden ? 'Hidden behind your code' : 'Keep it private'}
                  <span className="conv-menu__hint">Hide these letters behind a secret code</span>
                </span>
              </button>
              <button
                type="button"
                role="menuitem"
                className="conv-menu__item"
                disabled={setAside.busy}
                onClick={() => void setAside.hide()}
              >
                <EyeOff size={14} aria-hidden="true" />
                <span>
                  Hide from my list
                  <span className="conv-menu__hint">Back on your list when they next write</span>
                </span>
              </button>
              {canBlock && (
                <button
                  type="button"
                  role="menuitem"
                  className="conv-menu__item conv-menu__item--danger"
                  onClick={() => {
                    setOpen(false);
                    setConfirmingBlock(true);
                  }}
                >
                  <Ban size={14} aria-hidden="true" />
                  <span>
                    Block…
                    <span className="conv-menu__hint">Their letters stop reaching you. They aren't told</span>
                  </span>
                </button>
              )}
              {setAside.error && !confirmingBlock && (
                <p className="form-error conv-menu__error" role="alert">
                  <Info size={13} aria-hidden="true" /> {setAside.error}
                </p>
              )}
              <button
                type="button"
                role="menuitem"
                className="conv-menu__item"
                onClick={() => {
                  setOpen(false);
                  onOpenSummary();
                }}
              >
                <BookOpen size={14} aria-hidden="true" />
                <span>
                  Your story with @{name}
                  <span className="conv-menu__hint">The full page: where you met, notes, history</span>
                </span>
              </button>
              {(photos.length > 0 || rel?.capabilities.includes('photos')) && (
                <button
                  type="button"
                  role="menuitem"
                  className="conv-menu__item"
                  onClick={() => {
                    setOpen(false);
                    onOpenScrapbook();
                  }}
                >
                  <Images size={14} aria-hidden="true" />
                  <span>
                    Scrapbook
                    <span className="conv-menu__hint">
                      {photos.length > 0 ? `${photoCount(photos.length)} from your letters` : 'The photos you send each other'}
                    </span>
                  </span>
                </button>
              )}
              {stamps?.plus && rel?.status === 'accepted' && peer?.handle !== 'postmaster' && (
                <button
                  type="button"
                  role="menuitem"
                  className="conv-menu__item"
                  onClick={() => {
                    setOpen(false);
                    setGifting(true);
                  }}
                >
                  <Gift size={14} aria-hidden="true" />
                  <span>
                    Gift a stamp book
                    <span className="conv-menu__hint">It travels with your next letter</span>
                  </span>
                </button>
              )}
            </>
          )}
        </div>
      )}
      {gifting && <GiftStampBook relationshipId={relationshipId} onClose={() => setGifting(false)} />}
      {confirmingBlock && (
        <BlockConfirm
          peerName={name}
          busy={setAside.busy}
          error={setAside.error}
          onConfirm={() => void setAside.block()}
          onCancel={() => setConfirmingBlock(false)}
        />
      )}
    </div>
  );
}

/* Info strip adapts to the conversation's current state */
function ConversationInfoStrip({
  status,
  peerName: name,
  acknowledged = false,
  peerWriting = false,
  fromPostOffice = false,
  relationshipId,
  onRemoved,
}: {
  status: ConversationStatus;
  peerName: string;
  /** For a refused overture: take it off the list. */
  relationshipId?: string;
  onRemoved?: () => void;
  /** The latest letter is the Post Office's — it takes no replies. */
  fromPostOffice?: boolean;
  /** They chose to say a proper reply is coming (never inferred from a read). */
  acknowledged?: boolean;
  /** They have a saved draft reply to this letter (the pen). */
  peerWriting?: boolean;
}) {
  if (status === 'overture-pending') {
    return (
      <div className="info-strip info-strip--overture" role="status">
        <MailOpen size={14} className="info-strip__icon" aria-hidden="true" />
        <div className="info-strip__body">
          <span className="info-strip__title">Someone wrote to your address</span>
          This is first contact, not correspondence. Open it to accept and invite them, or to
          decline.
        </div>
      </div>
    );
  }
  if (status === 'overture-declined') {
    return (
      <div className="info-strip info-strip--muted" role="status">
        <XCircle size={14} className="info-strip__icon" aria-hidden="true" />
        <div className="info-strip__body">
          <span className="info-strip__title">You declined this overture</span>
          Still here to read, but you cannot write to them. Undo the decline to reopen it, or
          take it off your list — you'll find it under Hidden &amp; blocked on your profile.
          {relationshipId && onRemoved && (
            <span className="info-strip__actions">
              <RemoveOvertureButton relationshipId={relationshipId} onGone={onRemoved} />
            </span>
          )}
        </div>
      </div>
    );
  }
  if (status === 'their-arrived') {
    return (
      <div className="info-strip" role="status">
        <Info size={14} className="info-strip__icon" aria-hidden="true" />
        <div className="info-strip__body">
          <span className="info-strip__title">A letter from @{name} has arrived</span>
          {fromPostOffice
            ? 'Tap it below to read it. The Post Office writes to you, so no reply is needed.'
            : 'Tap it below to read it. You can reply from inside the letter.'}
        </div>
      </div>
    );
  }
  // A letter in the post is said once, by its own postbox track on the bubble;
  // the write bar under the thread carries the one hint that goes with it.
  if (status === 'my-awaiting-reply' && acknowledged) {
    return (
      <div className="info-strip info-strip--ack" role="status">
        <Hourglass size={14} className="info-strip__icon" aria-hidden="true" />
        <div className="info-strip__body">
          <span className="info-strip__title">@{name} has your letter</span>
          They let you know a proper reply is on its way. Good letters take a little while.
          {peerWriting && ' They have already started it.'}
        </div>
      </div>
    );
  }
  if (status === 'my-awaiting-reply' && peerWriting) {
    return (
      <div className="info-strip info-strip--pen" role="status">
        <PenLine size={14} className="info-strip__icon" aria-hidden="true" />
        <div className="info-strip__body">
          <span className="info-strip__title">@{name} has started a reply</span>
          There's a draft on their desk. It will reach you once they've finished it and sent it
          on its way, so there's no need to nudge.
        </div>
      </div>
    );
  }
  if (status === 'my-awaiting-reply') {
    return (
      <div className="info-strip" role="status">
        <Bell size={14} className="info-strip__icon" aria-hidden="true" />
        <div className="info-strip__body">
          <span className="info-strip__title">Waiting for @{name} to reply</span>
          You can't write another letter yet. Send a nudge — a short phrase added to your last
          letter — if you'd like to prompt a response.
        </div>
      </div>
    );
  }
  return null;
}

/* The action bar at the bottom of the thread — varies by conversation status */
function ThreadActionBar({
  status,
  peerName: name,
  notes,
  relationshipId,
  lastLetter,
  composing,
  draftBody,
  onCompose,
  onCancelCompose,
  onComposeDone,
  onNudge,
  onOpenLetter,
  letters,
  myId,
}: {
  status: ConversationStatus;
  peerName: string;
  /** Show my private notes above the composer (not for the Post Office). */
  notes: boolean;
  relationshipId: string;
  lastLetter: ApiLetter | null;
  composing: boolean;
  /** An unsent draft for this conversation, kept in IndexedDB until sealed. */
  draftBody: string | null;
  onCompose: () => void;
  onCancelCompose: () => void;
  onComposeDone: () => void;
  onNudge: () => void;
  onOpenLetter: (l: ApiLetter) => void;
  letters: ApiLetter[];
  myId: string | undefined;
}) {
  // A refused overture is inert — the only honest thing to offer is the way back.
  if (status === 'overture-declined') {
    return (
      <div className="thread-write-bar thread-write-bar--closed">
        <span className="thread-write-closed">
          <XCircle size={13} aria-hidden="true" /> You declined this correspondence.
        </span>
      </div>
    );
  }

  // Pending overture: the decision lives on the card, so there is nothing to
  // write yet and no button to mis-press.
  if (status === 'overture-pending') {
    return (
      <div className="thread-write-bar thread-write-bar--closed">
        <span className="thread-write-closed">
          <MailOpen size={13} aria-hidden="true" /> Open the overture to accept or decline it.
        </span>
      </div>
    );
  }

  // A letter is written only as the first one, or once the last exchange is
  // done; a reply is written from inside the letter it answers. Anything else
  // (a birthday prompt, a composer left open) never opens one mid-exchange.
  if (composing && canWriteLetter(status)) {
    return (
      <div className="thread-write-bar">
        <ThreadCompose
          relationshipId={relationshipId}
          peerName={name}
          showNotes={notes}
          onDone={onComposeDone}
          onCancel={onCancelCompose}
        />
      </div>
    );
  }

  // Can write: new conversation, or the last exchange was completed (settled)
  if (status === 'empty' || status === 'settled') {
    return (
      <div className="thread-write-bar thread-write-bar--input">
        <button
          type="button"
          className={`thread-write-btn thread-write-btn--input${draftBody ? ' thread-write-btn--draft' : ''}`}
          onClick={onCompose}
        >
          {draftBody
            ? `Continue your draft: ${draftBody}`
            : status === 'empty' ? 'Write your first letter…' : `Write a letter to @${name}…`}
        </button>
        <button
          type="button"
          className="thread-write-round"
          onClick={onCompose}
          aria-label={draftBody ? 'Continue your draft' : status === 'empty' ? 'Write your first letter' : 'Write a letter'}
        >
          <PenLine size={18} aria-hidden="true" />
        </button>
      </div>
    );
  }

  // The Post Office writes to you; the API refuses a reply, so offer none.
  if (status === 'their-arrived' && lastLetter?.kind === 'system-letter') {
    return (
      <div className="thread-write-bar thread-write-bar--closed">
        <span className="thread-write-closed">
          <Mail size={13} aria-hidden="true" /> The Post Office writes to you. It doesn't take replies.
        </span>
      </div>
    );
  }

  // Their letter arrived — point them to it
  if (status === 'their-arrived') {
    const theirLetter = letters.findLast((l) => l.authorId !== myId && (l.state === 'delivered' || l.state === 'opened'));
    return (
      <div className="thread-write-bar">
        <button
          type="button"
          className="thread-write-btn thread-write-btn--delivered"
          onClick={() => theirLetter && onOpenLetter(theirLetter)}
        >
          <Mail size={15} aria-hidden="true" />
          {draftBody ? `Open @${name}'s letter to finish your reply` : `Open @${name}'s letter to reply`}
        </button>
      </div>
    );
  }

  // My letter arrived, no response — nudge only
  if (status === 'my-awaiting-reply') {
    return (
      <div className="thread-write-bar">
        <button type="button" className="thread-write-btn thread-write-btn--nudge" onClick={onNudge}
        >
          <Bell size={15} aria-hidden="true" />
          Nudge @{name}
        </button>
      </div>
    );
  }

  // In transit — the letter's own track already says so, and nothing can be
  // written until it lands. The bar keeps the one hint that goes with the wait.
  return (
    <div className="thread-write-bar thread-write-bar--post">
      <span className="thread-write-hint">
        {status === 'my-in-transit'
          ? `You can nudge @${name} once it arrives.`
          : lastLetter?.kind === 'system-letter'
            ? 'A letter from the Post Office, on its rounds. No reply needed.'
            : `You can reply once it arrives.`}
      </span>
      {draftBody && (
        <span className="thread-write-draft-note">Your draft is saved. You can finish it once a letter can be sent.</span>
      )}
    </div>
  );
}

/* ── Bubble components ───────────────────────────────────────────────────── */

/**
 * A letter in its envelope (stationery): on its way, or arrived and not yet
 * opened. The envelope stands in for the bubble; the label carries the
 * address, the stamp sits top right, and the journey runs underneath.
 */
function EnvelopeBubble({
  letter,
  mine,
  peerName: name,
  myName,
  onOpen,
}: {
  letter: ApiLetter;
  mine: boolean;
  peerName: string;
  myName: string | null;
  onOpen: () => void;
}) {
  const stamp = bubbleStamp(letter, mine);
  const travelling = letter.state === 'sealed' || letter.state === 'in_transit';
  const to = mine ? `To @${name}` : myName ? `To @${myName}` : 'To you';
  const from = mine ? (myName ? `from @${myName}` : 'from you') : `from @${name}`;
  return (
    <div className={`bubble ${mine ? 'bubble--mine' : 'bubble--theirs'} bubble--envelope`}>
      <button
        type="button"
        className="envelope-bubble"
        onClick={onOpen}
        aria-label={travelling ? `Letter ${mine ? 'to' : 'from'} @${name}, on its way` : `Open the letter from @${name}`}
      >
        <Envelope envelope={letter.envelope!} to={to} from={from} stamp={<LetterStamp letter={letter} />} compact>
          {!travelling && !mine && (
            <span className="envelope__cta">
              Arrived{keptPhotos(letter.media).length > 0 ? ` · ${photoCount(keptPhotos(letter.media).length)} inside` : ''} · tap to open
            </span>
          )}
        </Envelope>
        {travelling && (
          <InThePost state={letter.state} sealedAt={letter.sealedAt} collectedAt={letter.collectedAt} deliverAt={letter.deliverAt} postageClass={letter.postageClass} />
        )}
        <span className="bubble__meta">
          <PaperSwatch letter={letter} />
          {stamp && (
            <span className="bubble__time">
              {stamp.label} {stamp.text}
            </span>
          )}
          {travelling && <Countdown deliverAt={letter.deliverAt} />}
        </span>
      </button>
    </div>
  );
}

/** A small swatch of the paper a letter was written on — nothing heavier in the thread. */
function PaperSwatch({ letter }: { letter: ApiLetter }) {
  const p = letter.stationery;
  if (!p) return null;
  return (
    <span
      className="paper-swatch"
      style={{ backgroundImage: `url(${p.imageUrl})` }}
      title={`Written on ${p.name}`}
      role="img"
      aria-label={`Written on ${p.name}`}
    />
  );
}

type ThreadItem =
  | { kind: 'letter'; key: string; at: string | null; letter: ApiLetter }
  | { kind: 'parcel'; key: string; at: string | null; parcel: StampParcel };

/**
 * Letters in their own order, with gift cards slotted in by when they were
 * posted (mine) or arrived (theirs) — the same moment their day chip shows.
 */
function threadItems(letters: ApiLetter[], parcels: StampParcel[], myId: string | undefined): ThreadItem[] {
  const out: ThreadItem[] = letters.map((l) => ({ kind: 'letter', key: l.id, at: letterDay(l, myId), letter: l }));
  for (const p of parcels) {
    const at = p.mine ? p.sentAt : (p.deliveredAt ?? p.sentAt);
    const t = new Date(at).getTime();
    const i = out.findIndex((o) => o.at != null && new Date(o.at).getTime() > t);
    out.splice(i < 0 ? out.length : i, 0, { kind: 'parcel', key: `parcel-${p.id}`, at, parcel: p });
  }
  return out;
}

/**
 * A gift card in the postal record: a small parcel, not a letter. Mine shows
 * the book and the note with its journey; theirs is only "something small on
 * its way" until it arrives, then the book, the note, and where to find it.
 */
function ParcelBubble({ parcel: p, peerName: name }: { parcel: StampParcel; peerName: string }) {
  const travelling = p.state === 'travelling';
  const opened = p.collectionName != null;
  const collected = p.collectedAt && new Date(p.collectedAt).getTime() <= Date.now() ? new Date(p.collectedAt) : null;
  const when = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short' }) : '';
  return (
    <div className={`bubble ${p.mine ? 'bubble--mine' : 'bubble--theirs'} parcel`}>
      <div className="parcel__card">
        <span className="parcel__corner" title="Sent 2nd class">
          <FrankedStamp cls="second" size="sheet" date={collected} design={opened ? (p.designs[0] ?? null) : null} />
        </span>
        <span className="parcel__kind">
          <Gift size={12} aria-hidden="true" /> {p.mine ? 'Stamp book you sent' : opened ? `Stamp book from @${name}` : 'A small parcel'}
        </span>
        <span className="parcel__title">
          {opened ? p.collectionName : `Something small is on its way from @${name}.`}
        </span>
        {opened && p.designs.length > 1 && (
          <span className="parcel__stamps" aria-hidden="true">
            {p.designs.slice(1).map((d) => (
              <PostageStamp key={d.id} cls="second" size="medium" design={d} />
            ))}
          </span>
        )}
        {p.note && <p className="parcel__note">{p.note}</p>}
        <span className="parcel__meta">
          {travelling
            ? p.deliverAt
              ? `In the post · arrives ${arrivesPhrase(p.deliverAt)}`
              : 'In the post'
            : p.mine
              ? `Delivered ${when(p.deliveredAt)}`
              : `Arrived ${when(p.deliveredAt)}`}
        </span>
        {!p.mine && !travelling && (
          <button type="button" className="linkish parcel__open" onClick={() => (window.location.hash = STAMP_BOOK_HASH)}>
            See it in your stamp book
          </button>
        )}
      </div>
    </div>
  );
}

/** The day a letter is filed under in the thread — the same stamp its bubble shows. */
function letterDay(l: ApiLetter | undefined, myId: string | undefined): string | null {
  if (!l) return null;
  if (l.authorId === myId) return l.sealedAt ?? null;
  return l.deliveredAt ?? l.sealedAt ?? null;
}

/** "Today" / "Yesterday" / "12 Sep" — the thread's date chips. */
function dayLabel(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], {
    day: 'numeric',
    month: 'short',
    year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  });
}


function SealedBubble({
  letter,
  mine,
  onOpen,
}: {
  letter: ApiLetter;
  mine: boolean;
  onOpen: () => void;
}) {
  const stamp = bubbleStamp(letter, mine);
  const taster = tasterPhrase(letter.taster);
  return (
    <div className={`bubble ${mine ? 'bubble--mine' : 'bubble--theirs'}`}>
      <button
        type="button"
        className="sealed-envelope"
        onClick={onOpen}
      >
        <LetterStamp letter={letter} />
        <div className="sealed-envelope__top">
          <span className="sealed-envelope__label">
            <Lock size={10} aria-hidden="true" />
            {mine ? 'your letter' : 'incoming letter'}
          </span>
          <StatePill state={letter.state} />
        </div>
        <div className="sealed-envelope__preview">
          {letter.body ? (
            <p className="sealed-envelope__snippet">{letter.body}</p>
          ) : (
            <p className="sealed-envelope__snippet" style={{ fontStyle: 'italic', opacity: 0.5 }}>
              content hidden until arrival
            </p>
          )}
          <div className="sealed-envelope__veil" aria-hidden="true">
            <Lock size={14} className="sealed-envelope__lock" />
          </div>
        </div>
        {taster && <p className="sealed-envelope__taster">{taster}</p>}
        <InThePost state={letter.state} sealedAt={letter.sealedAt} collectedAt={letter.collectedAt} deliverAt={letter.deliverAt} postageClass={letter.postageClass} />
        <div className="sealed-envelope__bottom">
          <PaperSwatch letter={letter} />
          {stamp && (
            <span className="bubble__time">
              {stamp.label} {stamp.text}
            </span>
          )}
          <span className="sealed-envelope__badges">
            <Countdown deliverAt={letter.deliverAt} />
            {letter.quality?.sentiment && <SentimentPill sentiment={letter.quality.sentiment} />}
          </span>
        </div>
      </button>
    </div>
  );
}

function ReadableBubble({
  letter,
  mine,
  onOpen,
}: {
  letter: ApiLetter;
  mine: boolean;
  onOpen: () => void;
}) {
  const stamp = bubbleStamp(letter, mine);
  const lastOpened = mine && letter.reads.length > 0 ? letter.reads[letter.reads.length - 1] : null;
  return (
    <div className={`bubble ${mine ? 'bubble--mine' : 'bubble--theirs'}`}>
      <button
        type="button"
        className="bubble__btn"
        onClick={onOpen}
        aria-label="Open letter detail"
      >
        <span className="bubble__text">
          <LetterStamp letter={letter} />
          {letter.body.replace(/\s+/g, ' ').slice(0, 140)}
          {letter.body.length > 140 ? '…' : ''}
        </span>
        {(letter.state === 'sealed' || letter.state === 'in_transit') && (
          <InThePost state={letter.state} sealedAt={letter.sealedAt} collectedAt={letter.collectedAt} deliverAt={letter.deliverAt} postageClass={letter.postageClass} />
        )}
        <span className="bubble__meta">
          <PaperSwatch letter={letter} />
          {stamp && (
            <span className="bubble__time">
              {stamp.label} {stamp.text}
            </span>
          )}
          {!mine && letter.subject && <span className="bubble__postmark">by post</span>}
          <StatePill state={letter.state} />
          {letter.quality?.sentiment && <SentimentPill sentiment={letter.quality.sentiment} />}
          {keptPhotos(letter.media).length > 0 && !letter.peek && (mine || isVisibleToRecipient(letter.state)) && (
            <span className="pill pill--photos" title="Photos came with this letter">
              <Camera size={9} aria-hidden="true" /> {photoCount(keptPhotos(letter.media).length)}
            </span>
          )}
          {/* Read count: only visible to the sender, once the recipient has opened it */}
          {lastOpened && (
            <span
              className="pill pill--reads"
              title={`Last opened ${formatFullStamp(lastOpened)} · ${letter.reads.length} time${letter.reads.length !== 1 ? 's' : ''}`}
            >
              <BookOpen size={9} aria-hidden="true" /> read {letter.reads.length} {letter.reads.length === 1 ? 'time' : 'times'}
              <span className="bubble__read-time">{formatStamp(lastOpened)}</span>
            </span>
          )}
          {mine && (letter.peekCount ?? 0) > 0 && (
            <span
              className="pill pill--peeks"
              title="Times they came back to this letter before it arrived"
            >
              <Eye size={9} aria-hidden="true" /> seen {letter.peekCount}×
            </span>
          )}
          {mine && <AcknowledgedNote letter={letter} peerName={letter.peer?.handle ?? 'they'} compact />}
          {/* Show nudge count if any */}
          {letter.addendums?.length > 0 && (
            <span className="pill pill--reads">
              <Bell size={9} aria-hidden="true" /> {letter.addendums.length} nudge{letter.addendums.length !== 1 ? 's' : ''}
            </span>
          )}
        </span>
      </button>
    </div>
  );
}

/* ── Level 3 — Letter detail ─────────────────────────────────────────────── */

function LetterDetailScreen({
  letterId,
  relationshipId,
  peer,
  openNudge,
  onBack,
}: {
  letterId: string;
  relationshipId: string;
  peer: RelationshipWithPeer['peer'];
  openNudge?: boolean;
  onBack: () => void;
}) {
  const { data: sessionUser } = useGetSessionQuery();
  const { data: thread } = useThreadQuery({ relationshipId });
  const [openLetter] = useOpenMutation();
  // Keepsake mode (F10): the same letter, alone — full screen, no chrome.
  const [keepsake, setKeepsake] = useState(false);
  const keepsakeRef = useRef<HTMLDivElement>(null);
  const keepsakeBtnRef = useRef<HTMLButtonElement>(null);

  // A modal contract: focus moves in, Tab is trapped, Esc closes, focus is
  // restored, and the body can't scroll behind the fixed overlay.
  useEffect(() => {
    if (!keepsake) return;
    keepsakeRef.current?.focus();
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setKeepsake(false);
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = keepsakeRef.current?.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables || focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      keepsakeBtnRef.current?.focus();
    };
  }, [keepsake]);

  const letters = useMemo(() => thread ?? [], [thread]);
  const letter = useMemo(
    () => letters.find((l) => l.id === letterId) ?? null,
    [letters, letterId],
  );

  // Every visit to an arrived letter is a read the writer is told about (§7.x),
  // not just the first — re-reads after it is `opened` (or answered) count too.
  // Once per screen, per letter: the open invalidates the thread, and that
  // refetch is not a new visit.
  const openedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!letter || !sessionUser) return;
    if (letter.authorId === sessionUser.id) return;
    if (letter.state !== 'delivered' && letter.state !== 'opened' && letter.state !== 'answered') return;
    if (openedRef.current === letter.id) return;
    openedRef.current = letter.id;
    void openLetter({ id: letter.id }).catch(() => {});
  }, [letter, sessionUser, openLetter]);

  const [touchLetter] = useTouchMutation();
  // Coming back to a sealed letter is a visit the writer is told about (§7.x).
  // Once per screen, per letter: a refetch or a re-render is not a new visit,
  // and the server would cool down the duplicate anyway.
  const touchedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!letter) return;
    if (letter.authorId === sessionUser?.id) return;
    if (letter.state !== 'sealed' && letter.state !== 'in_transit') return;
    if (touchedRef.current === letter.id) return;
    touchedRef.current = letter.id;
    // Fire-and-forget: the count is a courtesy to the writer, never a gate on
    // reading what the snippet already shows.
    touchLetter({ id: letter.id }).catch(() => {});
  }, [letter, sessionUser, touchLetter]);

  if (!letter) return null;

  const myId = sessionUser?.id;
  const mine = letter.authorId === myId;
  const name = peerName(peer);
  const isSealed = letter.state === 'sealed' || letter.state === 'in_transit' || letter.peek;
  const status = getConversationStatus(letters, myId);
  const lastLetter = letters[letters.length - 1];

  // Reply is visible on inbox letters that have arrived. It's enabled only when
  // this letter IS the conversation's current "last received" and there's no
  // pending outgoing letter already (i.e. we haven't replied yet).
  const isThisTheLastLetter = lastLetter?.id === letter.id;
  const arrived = letter.state === 'delivered' || letter.state === 'opened';
  // Overture: no Reply at all. The decision is accept-or-decline, and the card
  // owns that. Passing `replyVisible` here would re-enable what LetterRow hides.
  const replyVisible = !mine && arrived && letter.kind !== 'overture' && letter.kind !== 'system-letter';
  const replyEnabled = replyVisible && isThisTheLastLetter && status === 'their-arrived';

  // Nudge: only on the last outgoing letter that arrived but hasn't been answered.
  const canNudge =
    mine &&
    isThisTheLastLetter &&
    (letter.state === 'delivered' || letter.state === 'opened') &&
    status === 'my-awaiting-reply';

  // The full words are keepable only when the letter is fully readable — a
  // peek (sealed / in-transit) is still a promise, not a keepsake.
  const canKeep = !letter.peek && !isSealed;

  return (
    <section className="screen" aria-label="Letter detail">
      <header className="screen__header">
        <button
          type="button"
          className="screen__back"
          onClick={onBack}
          aria-label="Back to conversation"
        >
          <ArrowLeft size={20} />
        </button>
        <span className="screen__title">
          {letter.kind === 'overture'
            ? 'Overture'
            : mine
              ? `Your letter to @${name}`
              : `From @${name}`}
        </span>
        {canKeep && (
          <button
            ref={keepsakeBtnRef}
            type="button"
            className="screen__keepsake"
            onClick={() => setKeepsake(true)}
            aria-label="Read in keepsake mode"
            title="Read this letter alone, full screen — nothing else on the page"
          >
            <BookOpen size={16} aria-hidden="true" />
          </button>
        )}
      </header>

      {/* Contextual info strips */}
      {isSealed && (
        <div className="info-strip" role="status">
          <Lock size={14} className="info-strip__icon" aria-hidden="true" />
          <div className="info-strip__body">
            <span className="info-strip__title">
              {mine ? 'This letter is sealed and traveling' : "This letter hasn't arrived yet"}
            </span>
            {mine
              ? 'You can still revise or stop it while sealed. Once in flight, it cannot be recalled.'
              : 'Only the opening line is visible until the letter arrives. Come back as often as you like — each visit is counted.'}
          </div>
        </div>
      )}
      {!mine && letter.state === 'delivered' && letter.kind !== 'overture' && (
        <div className="info-strip" role="status">
          <Info size={14} className="info-strip__icon" aria-hidden="true" />
          <div className="info-strip__body">
            <span className="info-strip__title">This letter just arrived</span>
            Take your time reading it. When you're ready, reply below.
          </div>
        </div>
      )}
      {letter.subject && (
        <div className="info-strip info-strip--subject">
          <Mail size={14} className="info-strip__icon" aria-hidden="true" />
          <div className="info-strip__body">
            <span className="info-strip__title">Subject</span>
            {letter.subject}
          </div>
        </div>
      )}

      <div className="letter-detail">
        <LetterRow
          letter={letter}
          mode={mine ? 'outbox' : 'inbox'}
          replyVisible={replyVisible}
          replyEnabled={replyEnabled}
          canNudge={canNudge}
          openNudge={openNudge}
        />
        {letter.kind === 'letter' && <NotesCard relationshipId={relationshipId} peerName={name} />}
      </div>

      {/* Keepsake mode (F10): the letter alone — full screen, no chrome. */}
      {keepsake && canKeep && (
        <div
          className="keepsake"
          role="dialog"
          aria-modal="true"
          aria-label="Keepsake reading"
          ref={keepsakeRef}
          tabIndex={-1}
        >
          <header className="keepsake__head">
            <span className="keepsake__from">
              {mine ? `You, to @${name}` : `From @${name}`} · {formatFullStamp(letter.sealedAt ?? null)}
            </span>
            <button
              type="button"
              className="keepsake__close"
              onClick={() => setKeepsake(false)}
              aria-label="Close keepsake"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </header>
          <article className="keepsake__letter">
            {letter.body}
            <LetterSignature
              path={letter.signature}
              label={mine ? 'Your signature' : `Signed by @${name}`}
            />
          </article>
        </div>
      )}
    </section>
  );
}

/* ── Inline compose (deliberate, toggled by "Write a letter" button) ─────── */

function ThreadCompose({
  relationshipId,
  peerName: name,
  showNotes,
  onDone,
  onCancel,
}: {
  relationshipId: string;
  peerName: string;
  showNotes: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  const dispatch = useAppDispatch();
  const [sealLetter, { isLoading }] = useSealMutation();
  const [error, setError] = useState<string | null>(null);
  const [showPostageModal, setShowPostageModal] = useState(false);
  const [sentAs, setSentAs] = useState<ApiLetter | null>(null);
  const [showPushInvite, setShowPushInvite] = useState(false);
  const [sealing, setSealing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const postage = usePostageChoice(relationshipId);
  const draft = useAppSelector((s) => s.drafts.byId[relationshipId]);
  const body = draft?.body ?? '';
  // The floor is server-enforced (LettersService.assertSendable); this mirrors
  // it so the button reflects the truth rather than letting the API refuse.
  const progress = useLetterReadiness(body);
  const paste = usePasteGuard();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Resuming a draft: put the caret after the last word, not before the first.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  const onChange = useCallback(
    (next: string) => {
      // The clock starts at the first keystroke and is kept on every save
      // after it, so the sealed letter can say how long it took to write (F5).
      const d = {
        id: relationshipId,
        relationshipId,
        body: next,
        updatedAt: Date.now(),
        startedAt: draft?.startedAt ?? Date.now(),
      };
      dispatch(updated(d));
      void dispatch(persistDraft(d));
    },
    [dispatch, relationshipId, draft?.startedAt],
  );

  // After a seal: the postage explainer (first time only), then a one-time
  // invitation to hear about replies, then back to the thread.
  const afterPostage = useCallback(() => {
    if (shouldInviteToNotifications()) setShowPushInvite(true);
    else onDone();
  }, [onDone]);

  const finishSeal = useCallback(() => {
    let seen = false;
    try { seen = !!localStorage.getItem('toiyp:postage_intro'); } catch {}
    if (seen) {
      afterPostage();
    } else {
      setShowPostageModal(true);
    }
  }, [afterPostage]);

  const dismissPostageModal = useCallback(() => {
    try { localStorage.setItem('toiyp:postage_intro', '1'); } catch {}
    setShowPostageModal(false);
    afterPostage();
  }, [afterPostage]);

  const dismissPushInvite = useCallback(() => {
    setShowPushInvite(false);
    onDone();
  }, [onDone]);

  const onSubmit = useCallback(async () => {
    if (!progress.sendable) return;
    setError(null);
    const asked = postage.selected;
    try {
      // The seal moment: the postmark lands while the letter goes off.
      const stamped = postage.enabled || postage.design != null;
      setSealing(stamped);
      const [sealed] = await Promise.all([
        sealLetter({
          relationshipId,
          body,
          postage: asked,
          composeMs: composeMsOf(draft),
          stampDesignId: postage.design?.id,
          stationeryId: postage.paper?.id,
          envelopeId: postage.envelope?.id,
        }).unwrap(),
        stamped ? sealPause() : Promise.resolve(),
      ]);
      setSealing(false);
      trackLetterSealed();
      void dispatch(forgetDraft(relationshipId));
      void dispatch(flushPendingSeals());
      setSentAs(sealed);
      if (sealed.postageDowngraded) postageNotice.show(downgradeNotice(asked, sealed.postageClass));
      finishSeal();
    } catch (e) {
      const status = (e as { status?: unknown } | undefined)?.status;
      if (status === 'FETCH_ERROR' || status === 'PARSING_ERROR') {
        setSealing(false);
        await dispatch(
          enqueueSeal({
            id: crypto.randomUUID(),
            relationshipId,
            body,
            queuedAt: Date.now(),
            postage: asked,
            stampDesignId: postage.design?.id,
            stationeryId: postage.paper?.id,
            envelopeId: postage.envelope?.id,
            // Frozen here: the draft is forgotten below, and the replay has to
            // still be able to claim how long the writing took.
            composeMs: composeMsOf(draft),
          }),
        );
        void dispatch(forgetDraft(relationshipId));
        finishSeal();
      } else {
        setSealing(false);
        setError(apiErrorMessage(e, 'Could not seal the letter'));
      }
    }
  }, [dispatch, sealLetter, relationshipId, body, draft, finishSeal, progress.sendable, postage.selected, postage.enabled, postage.design, postage.paper, postage.envelope]);

  const canSend = !isLoading && progress.sendable;

  return (
    <div className={`thread-compose-inline${expanded ? ' thread-compose-inline--sheet' : ''}`}>
      <div className="thread-compose-inline__header">
        <span>Write a letter</span>
        <span className="thread-compose-inline__tools">
          <button
            type="button"
            className="icon-btn"
            style={{ width: 26, height: 26 }}
            onClick={() => setExpanded((v) => !v)}
            aria-pressed={expanded}
            aria-label={expanded ? 'Shrink the page' : 'Write full screen'}
            title={expanded ? 'Shrink the page' : 'Write full screen'}
          >
            {expanded ? <Minimize2 size={14} aria-hidden="true" /> : <Maximize2 size={14} aria-hidden="true" />}
          </button>
          <button type="button" className="icon-btn" style={{ width: 26, height: 26 }} onClick={onCancel} aria-label="Close (your draft is kept)" title="Close (your draft is kept)">
            <X size={14} />
          </button>
        </span>
      </div>
      {showNotes && <ComposerNotes relationshipId={relationshipId} peerName={name} />}
      <label htmlFor={`compose-${relationshipId}`} className="visually-hidden">Your letter</label>
      <div className={`compose-envelope${postage.enabled || postage.hasDesigns ? ' compose-envelope--stamped' : ''}${sealing ? ' compose-envelope--sealing' : ''}`}>
        <textarea
          id={`compose-${relationshipId}`}
          ref={textareaRef}
          className="thread-compose__textarea"
          style={{ borderRadius: 'var(--r-lg)' }}
          value={body}
          onChange={(e) => {
            paste.clearNotice();
            onChange(e.target.value);
          }}
          onPaste={paste.onPaste}
          onDrop={paste.onDrop}
          placeholder="Write as if they will read it twice…"
          rows={8}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onCancel();
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canSend) {
              e.preventDefault();
              void onSubmit();
            }
          }}
        />
        <ComposeStamp choice={postage} peerName={name} sealing={sealing} />
      </div>
      {error && <p className="thread-compose__error">{error}</p>}
      <ComposeProgress progress={progress} notice={paste.notice} />
      <PaperPreviewButton paper={postage.paper} text={body} />
      <div className="thread-compose-inline__actions">
        <span className="thread-compose-inline__hint">
          <SavedAgo at={draft?.updatedAt} /> · {progress.sendable ? '⌘↵ to seal' : progress.ready ? 'not quite yet' : `${progress.remaining} more to seal`}
        </span>
        <button
          type="button"
          className="btn btn--primary"
          disabled={!canSend}
          title={
            canSend
              ? undefined
              : progress.ready
                ? progress.quality.message
                : `Write at least ${progress.required} characters to seal this letter`
          }
          onClick={() => void onSubmit()}
          style={{ borderRadius: 'var(--r-full)', gap: 6 }}
        >
          <Send size={13} aria-hidden="true" />
          {isLoading ? 'Sealing…' : sealLabel('Seal & send', postage)}
        </button>
      </div>
      <PostageOfferModal choice={postage} peerName={name} />
      {showPostageModal && (
        <PostageModal
          onDismiss={dismissPostageModal}
          sent={postage.enabled && sentAs ? { postageClass: sentAs.postageClass, deliverAt: sentAs.deliverAt } : null}
        />
      )}
      {showPushInvite && <NotificationInvite onDone={dismissPushInvite} />}
    </div>
  );
}
